import { AcceptedResponseCodes } from '../Enum/ResponseCodes';
import { apiReturnedResponseCode } from '../Store/App/action';
import { ErrorResponse } from '../Interface/ErrorResponse';
import { ServiceContainer } from './ServiceContainer/ServiceContainer';
import { File } from '../Interface/Response/Distribution/File';
import { StatusCodes } from 'http-status-codes';

const getProductionUrl = (): string => {
    return process.env.REACT_APP_API_URL as string;
};

const getDevelopUrl = (): string => {
    return 'https://fds2.fdta.dev/';
};

export const getApiUrl = (): string => {
    return process.env.NODE_ENV === 'production' ? getProductionUrl() : getDevelopUrl();
};

const getJsonContent = <T>(content: string): T => {
    try {
        return JSON.parse(content);
    } catch (e) {
        return {} as T;
    }
};

const getOptions = (method: string, body?: string, token?: string, retryCount?: number): RequestInit => {
    const options: RequestInit = { method, body };

    options.headers = {
        'X-User-Hash': ServiceContainer.storeGateway.getState().app.userId,
    };

    if (method === 'POST') {
        options.headers = {
            ...options.headers,
            Accept: 'application/json',
            'Content-Type': 'application/json',
        };
    }

    if (token) {
        options.headers = {
            ...options.headers,
            Authorization: `${token}`,
        };
    }

    if (retryCount) {
        options.headers = {
            ...options.headers,
            'X-Retry-Count': retryCount.toString(),
        };
    }

    return options;
};

const request = async <T>(
    uri: string,
    method: string,
    body?: string,
    token?: string,
    timeout?: number,
    retryCount?: number,
): Promise<T> => {
    const options = getOptions(method, body, token, retryCount);

    let rawResponse: Response;

    if (timeout !== undefined && timeout > 0) {
        try {
            const abortController = new AbortController();
            const abortCallback = setTimeout(() => abortController.abort(), timeout);
            rawResponse = await fetch(getApiUrl() + uri, {
                ...options,
                signal: abortController.signal,
            });
            clearTimeout(abortCallback);
        } catch (error) {
            return Promise.reject({ code: StatusCodes.REQUEST_TIMEOUT });
        }
    } else {
        rawResponse = await fetch(getApiUrl() + uri, options);
    }

    if (Object.values(AcceptedResponseCodes).includes(rawResponse.status)) {
        ServiceContainer.storeGateway.dispatch(apiReturnedResponseCode(rawResponse.status, 'Ok'));
        return Promise.resolve(getJsonContent<T>(await rawResponse.text()));
    } else {
        const response = getJsonContent<ErrorResponse>(await rawResponse.text());
        ServiceContainer.storeGateway.dispatch(apiReturnedResponseCode(rawResponse.status, response.message));
        return Promise.reject({ code: rawResponse.status, body: response });
    }
};

export const get = <T>(uri: string, token?: string, timeout?: number, retryCount?: number): Promise<T> => {
    return request(uri, 'GET', undefined, token, timeout, retryCount);
};

export const post = <T>(uri: string, body: string, token?: string): Promise<T> => {
    return request(uri, 'POST', body, token);
};

export const getWithToken = <T>(uri: string): Promise<T> => {
    const { storeGateway } = ServiceContainer;
    return get<T>(uri, storeGateway.getState().auth.token);
};

export const getFile = async (url: string): Promise<File> => {
    const options = getOptions('GET', undefined, ServiceContainer.storeGateway.getState().auth.token);

    const rawResponse = await fetch(url, options);

    if (Object.values(AcceptedResponseCodes).includes(rawResponse.status)) {
        ServiceContainer.storeGateway.dispatch(apiReturnedResponseCode(rawResponse.status, 'Ok'));

        const matches = rawResponse.headers?.get('Content-Disposition')?.match(/filename="(.*)"$/);
        const fileName = matches?.length === 2 ? matches[1].replace('_', ' ') : 'file';
        const blob = await rawResponse.blob();
        return Promise.resolve({ fileName, blob });
    } else {
        const response = getJsonContent<ErrorResponse>(await rawResponse.text());
        ServiceContainer.storeGateway.dispatch(apiReturnedResponseCode(rawResponse.status, response.message));
        return Promise.reject({ code: rawResponse.status, body: response });
    }
};
